import React, { useState } from 'react';
import { Filter, Dna, Globe, ChevronDown, ChevronUp, Microscope } from 'lucide-react';
import { DISEASE_CATEGORIES, getDiseaseCategoryCounts, snpMatchesDiseaseFilter } from '../utils/traitCategories';

/**
 * FilterBar
 *
 * Props:
 *   activeFilters       string[]   — active category filter IDs
 *   onFilterChange      fn         — called with new filter array
 *   categoryCounts      object     — count per category id
 *   showCarriedOnly     boolean    — whether "My Genotype" toggle is active
 *   onCarriedToggle     fn         — called with new boolean value
 *   ancestryFilter      string     — active ancestry filter value (GWAS Catalog broad_ancestry)
 *   onAncestryChange    fn         — called with new ancestry string or null to clear
 *   availableAncestries string[]   — ancestry values present in current result set
 */

// Map raw GWAS Catalog broad_ancestry strings to clean display labels.
// Partial matching is used so multi-word entries like "African American or
// Afro-Caribbean" still hit the right bucket.
const ANCESTRY_OPTIONS = [
  { value: 'European',                                    label: 'European' },
  { value: 'East Asian',                                  label: 'East Asian' },
  { value: 'African American or Afro-Caribbean',          label: 'African / Afro-Caribbean' },
  { value: 'South Asian',                                 label: 'South Asian' },
  { value: 'Hispanic or Latin American',                  label: 'Hispanic / Latin American' },
  { value: 'Multi-ancestry',                              label: 'Multi-ancestry' },
  { value: 'African unspecified',                         label: 'African (unspecified)' },
  { value: 'Greater Middle Eastern',                      label: 'Middle Eastern' },
  { value: 'South East Asian',                            label: 'South-East Asian' },
  { value: 'Native American',                             label: 'Native American' },
  { value: 'Oceanian',                                    label: 'Oceanian' },
  { value: 'Other',                                       label: 'Other' },
];

const FilterBar = ({
  activeFilters,
  onFilterChange,
  categoryCounts,
  showCarriedOnly = false,
  onCarriedToggle,
  ancestryFilter = null,
  onAncestryChange,
  availableAncestries = [],
  diseaseFilter = [],
  onDiseaseFilterChange,
  diseaseCategoryCounts = {},
}) => {
  const [ancestryExpanded, setAncestryExpanded] = useState(false);
  const [diseaseExpanded, setDiseaseExpanded] = useState(false);

  const categories = [
    { id: 'all',               label: 'All',          icon: '🔍', color: 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200' },
    { id: 'pathogenic',        label: 'Pathogenic',   icon: '🔴', color: 'bg-rose-50 hover:bg-rose-100 text-rose-800 border-rose-200' },
    { id: 'likely-pathogenic', label: 'Likely Path.', icon: '🟠', color: 'bg-orange-50 hover:bg-orange-100 text-orange-800 border-orange-200' },
    { id: 'risk',              label: 'Risk',         icon: '🟡', color: 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-200' },
    { id: 'protective',        label: 'Protective',   icon: '🟢', color: 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200' },
    { id: 'drug-response',     label: 'Drug',         icon: '💊', color: 'bg-violet-50 hover:bg-violet-100 text-violet-800 border-violet-200' },
    { id: 'neutral',           label: 'Neutral',      icon: '⚪', color: 'bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200' },
    { id: 'uncertain',         label: 'Uncertain',    icon: '🔵', color: 'bg-sky-50 hover:bg-sky-100 text-sky-800 border-sky-200' },
  ];

  const toggleFilter = (filterId) => {
    if (filterId === 'all') {
      onFilterChange(['all']);
      return;
    }
    let newFilters = [...activeFilters];
    if (newFilters.includes('all')) newFilters = [];
    if (newFilters.includes(filterId)) {
      newFilters = newFilters.filter(f => f !== filterId);
    } else {
      newFilters.push(filterId);
    }
    if (newFilters.length === 0) newFilters = ['all'];
    onFilterChange(newFilters);
  };

  const isActive = (filterId) =>
    activeFilters.includes(filterId) || activeFilters.includes('all');

  // Only show ancestry options that are actually present in the current data
  const visibleAncestries = ANCESTRY_OPTIONS.filter(opt =>
    availableAncestries.some(a => a && a.includes(opt.value))
  );

  const showAncestrySection = onAncestryChange && visibleAncestries.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50">
        <Filter className="w-4 h-4 text-indigo-600 flex-shrink-0" />
        <h3 className="text-sm font-bold text-slate-900">Filter</h3>
      </div>

      <div className="p-3 space-y-3">

        {/* ── My Genotype toggle ── */}
        {onCarriedToggle && (
          <>
            <button
              onClick={() => onCarriedToggle(!showCarriedOnly)}
              className={`
                w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg
                text-xs font-semibold transition-all border
                ${showCarriedOnly
                  ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm'
                  : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <Dna className="w-3.5 h-3.5 flex-shrink-0" />
                <span>My Genotype Only</span>
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                showCarriedOnly ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-600'
              }`}>
                {showCarriedOnly ? 'ON' : 'OFF'}
              </span>
            </button>
            {showCarriedOnly && (
              <p className="text-xs text-slate-500 px-1 leading-relaxed">
                Showing only variants where your alleles match the studied association.
              </p>
            )}
          </>
        )}

        {/* ── Study Population filter ── */}
        {showAncestrySection && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            {/* Collapsible header */}
            <button
              onClick={() => setAncestryExpanded(v => !v)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-slate-700">Study Population</span>
                {ancestryFilter && (
                  <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded font-medium">
                    Active
                  </span>
                )}
              </div>
              {ancestryExpanded
                ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              }
            </button>

            {ancestryExpanded && (
              <div className="p-2 space-y-1">
                <p className="text-xs text-slate-400 px-1 pb-1 leading-relaxed">
                  Filter GWAS associations by the ancestry of study participants.
                  Results from populations closer to your background may be more applicable.
                </p>

                {/* Clear option */}
                <button
                  onClick={() => onAncestryChange(null)}
                  className={`
                    w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors
                    ${!ancestryFilter
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                    }
                  `}
                >
                  All populations
                </button>

                {visibleAncestries.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => onAncestryChange(
                      ancestryFilter === opt.value ? null : opt.value
                    )}
                    className={`
                      w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors
                      ${ancestryFilter === opt.value
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                      }
                    `}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Disease Category filter ── */}
        {onDiseaseFilterChange && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setDiseaseExpanded(v => !v)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Microscope className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-slate-700">Disease Category</span>
                {diseaseFilter.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded font-medium">
                    {diseaseFilter.length}
                  </span>
                )}
              </div>
              {diseaseExpanded
                ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              }
            </button>

            {diseaseExpanded && (
              <div className="p-2 space-y-1">
                <p className="text-xs text-slate-400 px-1 pb-1 leading-relaxed">
                  Filter variants by associated disease area. A variant can appear in multiple categories.
                </p>
                <button
                  onClick={() => onDiseaseFilterChange([])}
                  className={`
                    w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors
                    ${diseaseFilter.length === 0
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                    }
                  `}
                >
                  All categories
                </button>
                {DISEASE_CATEGORIES.map(cat => {
                  const count = diseaseCategoryCounts[cat.id] || 0;
                  const isActive = diseaseFilter.includes(cat.id);
                  if (count === 0) return null;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        const next = isActive
                          ? diseaseFilter.filter(id => id !== cat.id)
                          : [...diseaseFilter, cat.id];
                        onDiseaseFilterChange(next);
                      }}
                      className={`
                        w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors
                        ${isActive
                          ? 'bg-indigo-600 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                        }
                      `}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span>{cat.icon}</span>
                        <span className="truncate">{cat.label}</span>
                      </span>
                      <span className={`text-xs flex-shrink-0 px-1 rounded ${isActive ? 'bg-white/20 text-white' : 'text-slate-400'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

   {/* ── Category buttons ── */}
        <div className="grid grid-cols-2 gap-2">
          {categories.map(category => {
            const count = categoryCounts?.[category.id] || 0;
            const active = isActive(category.id);

            return (
              <button
                key={category.id}
                onClick={() => toggleFilter(category.id)}
                disabled={count === 0 && category.id !== 'all'}
                className={`
                  px-2 py-2 rounded-lg text-xs font-semibold transition-all border text-left
                  ${active ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}
                  ${count === 0 && category.id !== 'all' ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                  ${category.color}
                `}
              >
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm flex-shrink-0">{category.icon}</span>
                    <span className="text-xs truncate">{category.label}</span>
                  </div>
                  {category.id !== 'all' && (
                    <span className="text-xs font-bold px-1 bg-white/70 rounded flex-shrink-0">
                      {count > 999 ? `${Math.floor(count / 1000)}k` : count}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default FilterBar;